import { makeAutoObservable, runInAction } from "mobx";
import type { Config } from "config/config";
import { ensured } from "helpers/value";
import { type AddressVerificationClient } from "services/addressVerification/addressVerificationClient";
import type { CompanyStore } from "services/company/companyStore";
import type { DirectorAddressStore } from "services/director/directorAddressStore";
import type { DirectorStore } from "services/director/directorStore";
import type { FeatureStore } from "services/feature/featureStore";

export class AddressVerificationStore {
  private _state: Record<string, boolean | undefined> = {};

  constructor(
    private readonly _config: Config,
    private readonly _companyStore: CompanyStore,
    private readonly _directorStore: DirectorStore,
    private readonly _addressStore: DirectorAddressStore,
    private readonly _addressVerificationClient: AddressVerificationClient,
    private readonly _featureStore: FeatureStore,
  ) {
    makeAutoObservable(this);
  }

  get isPoaRequired(): boolean {
    if (!this._strictPoaRules) {
      return this._isMainDirectorPoaRequiredLegacy();
    }
    return this._isMainDirectorPoaRequired();
  }

  async checkDirectorAddress(): Promise<boolean> {
    if (!this._strictPoaRules) {
      return await this._checkMainDirectorAddressLegacy();
    }

    const address = this._addressStore.tryGetForDirector(this.mainDirectorId);
    if (!address) {
      return true;
    }

    const needPoa = await this._getPoaRequirementForAddress(address.id);
    runInAction(() => {
      this._state[address.id] = needPoa;
    });

    return needPoa;
  }

  clearForDirector(directorId: string): void {
    const address = this._addressStore.tryGetForDirector(directorId);
    if (address) {
      Reflect.deleteProperty(this._state, address.id);
    }
  }

  private get mainDirectorId(): string {
    return this._directorStore.getMainDirector().id;
  }

  private _isMainDirectorPoaRequired(): boolean {
    const address = this._addressStore.tryGetForDirector(this.mainDirectorId);
    if (!address) {
      return true;
    }
    if (address.isPoaPassedChecks) {
      return false;
    }

    const cachedNeedPoa = this._state[address.id];
    return typeof cachedNeedPoa === "undefined" ? true : cachedNeedPoa;
  }

  // Strict POA rules (the electronic address-verification flow) are GB-only and behind the feature
  // flag; when off (AU always, or GB pre-rollout) the store keeps the exact legacy behaviour. Both
  // paths gate on the main director only — secondary-director address verification is out of scope.
  private get _strictPoaRules(): boolean {
    return !this._config.isAU && this._featureStore.hasFeature("formationPoaVerification");
  }

  private _isMainDirectorPoaRequiredLegacy(): boolean {
    if (this._featureStore.hasFeature("formationPoaVerification")) {
      const address = this._addressStore.tryGetForDirector(this.mainDirectorId);
      if (address?.isPoaPassedChecks) {
        return false;
      }
    } else {
      if (this._companyStore.getCompany().poaFileId) {
        return false;
      }
    }

    const isPoaRequired = this._state[this._getMainDirectorAddressId()];
    return typeof isPoaRequired === "undefined" ? true : isPoaRequired;
  }

  private async _checkMainDirectorAddressLegacy(): Promise<boolean> {
    const addressId = this._getMainDirectorAddressId();
    const isPoaRequired = await this._getPoaRequirementForAddress(addressId);
    runInAction(() => {
      this._state[addressId] = isPoaRequired;
    });

    return isPoaRequired;
  }

  private _getMainDirectorAddressId(): string {
    const address = ensured(this._addressStore.tryGetForDirector(this.mainDirectorId));
    return address.id;
  }

  private async _getPoaRequirementForAddress(addressId: string): Promise<boolean> {
    try {
      const result = await this._addressVerificationClient.getAddressVerification(addressId);
      return result.needPoa;
    } catch (e) {
      console.warn(`Couldn't load verification results for address ${addressId}`, e); // fail safe: default to requiring POA
      return true;
    }
  }
}
